import {
  FinancialAsset,
  LegalEntityWithRole,
  PersonSimpleWithRole,
  ProjectInterest,
  ProjectInterestRates,
} from './project.models';
import { LegalEntityRole } from './shared.enums';
import { FileResponse, PersonAddress } from './shared.models';

export interface ProjectIdeaCeationResponse {
  id: string;
  country: string;
  city: string;
  currency: string;
  name: string;
  description: string;
  files: FileResponse[];
  foundingMode?: number;
  contactUserId?: string | null;
}

export interface PczrtProjectClassification {
  id: string;
  code: string;
  description: string;
  isActive: boolean;
  isActiveAdmin: boolean;
  percentage: number;
  localizedName: string;
  isDeleted: boolean;
  version: string;
  creationDate: Date;
  creationUser: string;
  modificationDate: Date;
  modificationUser: string;
}

export interface ProjectResponse {
  id: string;
  foundingMode: number;
  state: number;
  name: string;
  country: string;
  city: string;
  currency: string;
  targetAmount: number;
  currentAmount: number;
  targetColletionEndDate: Date;
  offerMinAmount: number;
  offerMaxAmount: number;
  ownerCapital: number;
  loanTypeInterest: number;
  loanTypeYield: number;
  loanTypePaymentStart: Date;
  loanTypePaymentEnd: Date;
  classification: PczrtProjectClassification;
  mainImages: FileResponse[];
}

export interface PersonRequest {
  titleCode: string | null;
  firstName: string;
  midName: string | null;
  lastName: string;
  citizenship: string | null;
  birthPlace: string | null;
  birthDate: Date | null;
  email: string | null;
  position: string | null;
  motherFirstName: string | null;
  motherMidName: string | null;
  motherLastName: string | null;
  maidenFirstName: string | null;
  maidenMidName: string | null;
  maidenLastName: string | null;
  idCardType: number | null;
  idCardNumber: string | null;
  idCardNumberExpiry: Date | null;
  taxIdentifier: string | null;
  bankAccountNumber: string | null;
  address: PersonAddress | null;
}

export interface PersonProjectRequest {
  connectionTypeCode: string | null;
  description: string | null;
  person: PersonRequest;
}

export interface ProjectSetupARequestBase {
  companyName: string;
  companyTypeId: string;
  ownershipStructure: number;
  email: string;
  ownerAcceptEveryInfoGiven: boolean;
  description: string;
  teaorNumbers: string;
}
export interface ProjectSetupARequest extends ProjectSetupARequestBase {
  persons: PersonProjectRequest[];
}

export interface ProjectSetupAResponse extends ProjectSetupARequestBase {
  files: FileResponse[];
  persons: PersonSimpleWithRole[];
}

export interface ProjectSetupBResponse {
  targetAmount: number;
  currentAmount: number;
  targetColletionEndDate: string;
  offerMinAmount: number;
  offerMaxAmount: number;
  ownerCapital: number;
  incompleteCollectionResult: number | null;
}

export interface ProjectSetupCResponse {
  riskFactors: string;
}

export interface ProjectSetupDRequestBase {
  securitiesTotalOffer: number;
  securitiesType: number;
  securitieBookingPrice: number;
  oversubscriptionAcceptance: boolean;
  oversubscriptionType: number;
  isGuarantorLegalEntity: boolean;
  isInvestmentSecuredByThirdParty: boolean;
  isRepurchaseCommitment: boolean;
  repurchaseCommitmentAndDeadline: string;
  guaranteeOrSecurityInfo: string;
}
export interface ProjectSetupDRequest extends ProjectSetupDRequestBase {
  persons: PersonProjectRequest[];
}

export interface ProjectSetupDResponse extends ProjectSetupDRequestBase {
  financialAssets: FinancialAsset[] | null;
  persons: PersonSimpleWithRole[] | null;
}

export interface LegalEntityRequest {
  taxNumber: string;
  name: string;
  email: string;
  country: string;
  ownershipStructure: number | null;
  contactInfo: string | null;
  website: string | null;
  teaorNumbers: string | null;
  bankAccountNumber: string | null;
  companyTypeId: string | null;
  address: PersonAddress;
}

export interface ProjectSetupERequest {
  isSpecialPurposeCompanyBetweenOwnerAndInvestor: boolean;
  persons: PersonProjectRequest[];
  legalEntitySpecialPurpose?: LegalEntityRequest | undefined;
}

export interface ProjectSetupEResponse {
  isSpecialPurposeCompanyBetweenOwnerAndInvestor: boolean;
  persons: PersonSimpleWithRole[];
  legalEntitySpecialPurpose: LegalEntityWithRole[];
}

export interface ProjectSetupFRequest {
  keyRightsForTransferableSecuritiesOrCrowdfundingAssets: string;
  keyRightsForTransferableSecuritiesOrCrowdfundingAssetsDenial: string;
  keyRightsForTransferableSecuritiesOrCrowdfundingAssetsRestrictions: string;
  exitOptionsForInvestors: string;
  capitalAndVotingRightsDistributionBeforeAndAfterOffer: string;
}

export interface LegalEntityProjectRequest {
  connectionTypeCode: LegalEntityRole | null;
  legalEntity: LegalEntityRequest;
}

export interface ProjectSetupGBase {
  loanCoverage: number;
  loanUsage: number;
  loanTerm: number;
  loanInterestRatesType: number;
  loanInterestPaymentType: number;
  loanLengthMonths: number;
  conditions: string;
  loanTypeCapitalPaymentFrequency: number | null;
  loanTypeInterestPaymentFrequency: number | null;
  informationOnNatureAndConditionsOfGuarantee: string | null;
  informationOnNatureAndConditionsOfSurety: string | null;
  informationOnNatureAndConditionsOfWarranty: string | null;
  informationOnLoanAndNonCompliance: string | null;
  informationOnLoanAndItsManagement: string | null;
  persons: PersonProjectRequest[];
}

export type LoanInterests = {
  interest: ProjectInterest[];
};

export interface ProjectSetupGRequest extends ProjectSetupGBase {
  loanInterests: LoanInterests;
  legalEntities: LegalEntityProjectRequest[];
}

export interface ProjectSetupGResponse extends ProjectSetupGBase {
  loanTypeInterest: number;
  loanTypeYield: number;
  interestRates: ProjectInterestRates;
  legalEntitySpecialPurpose: LegalEntityWithRole[];
  financialAssetsSectionG?: FinancialAsset[];
}

export interface ProjectSetupHResponse {
  feesAndCostsChargedToInvestorIncludingAdministrativeCostsFromSaleOfCrowdfundingAssets:
    | string
    | null;
  whereAndHowToObtainFreeInformationOnCrowdfundingProjectProjectOwnerAndSpecialPurposeEntity:
    | string
    | null;
  howAndWhereInvestorCanComplainAboutInvestmentOrConductOfProjectOwnerOrCrowdfundingServiceProvider:
    | string
    | null;
  exitOptionsForInvestor: string | null;
}

export interface ProjectSetupIResponse {
  crowdfundingServiceProviderDetails: string | null;
  minAndMaxInterestRatesForLoansAvailableToIndividualInvestorPortfolios:
    | string
    | null;
  minAndMaxLoanTermsForIndividualInvestorPortfolios: string | null;
  creditRiskCategoriesDistributionDefaultRatesAndWeightedAverageInterestRatesByRiskCategoryAndYear:
    | string
    | null;
  keyElementsOfInternalMethodologyForCreditRiskAssessmentAndRiskCategoryDeterminationForIndividualCrowdfundingProjects:
    | string
    | null;
  annualizedTargetRateAndConfidenceIntervalConsideringFeesAndDefaultRates:
    | string
    | null;
  proceduresInternalMethodologiesAndCriteriaForSelectingCrowdfundingProjectsForIndividualInvestorPortfolios:
    | string
    | null;
  coverageAndConditionsOfApplicableCapitalGuarantees: string | null;
  managementOfPortfolioLoansIncludingDefaultByProjectOwner: string | null;
  riskSharingStrategies: string | null;
  feesPayableByProjectOwnerOrInvestorIncludingDeductionsFromInterestPayableByProjectOwner:
    | string
    | null;
}

export enum WizardSection {
  Base = 'Base',
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',
  F = 'F',
  G = 'G',
  H = 'H',
  I = 'I',
  J = 'J',
}
